import { ClientSubscription, OpaSubscription } from '@models/Opa';
import {
  isProductAnalyzerSubscription,
  isRetailInsightsOrMarketPremiumSubscription,
  isRetailKadSubscription,
  ProductDataType,
  ProductType,
} from '@pages/product-analyzer/Product';
import { Country, Option, PageName } from './pageConfig/Types';
import { ErrorTagEnum, saveErrorLog } from './LogUtil';
import { RetailerClientState } from '@state/reducers/retailer-client/RetailerClientReducer';
import { HitlistsRetailerClientState } from '@state/reducers/hitlist-retailer-client/HitlistsRetailerClientReducer';
import { SellerInsightsRetailerClientState } from '@state/reducers/seller-insights-retailer-client/SellerInsightsRetailerClientReducer';
import { cloneDeep, sortBy, sortedUniqBy } from 'lodash';
import { constructRetailerClientState } from '@state/reducers/retailer-client/RetailerClientUtils';
import { constructRetailerList } from './FilterUtils';
import { getSavedFilters } from '@services/SavedFilters.service';
import { appliedFiltersInitialState, PagesConfig } from './pageConfig/PagesConfigContext';
import { getFilters } from '@services/Filters.service';
import { MainFilters } from '@services/FiltersTypes';
import { getRetailerClientByClientId } from '@services/RetailerClient.service';
import { RetailerClient } from '@models/RetailerClient';
import { InitialFiltersState } from '@state/reducers/initial-filter/InitialFiltersReducer';
import { InitialFiltersProductsState } from '@state/reducers/initial-filter-products/InitialFiltersProductsReducer';
import { SideFiltersState } from '@state/reducers/side-filters/SideFiltersReducer';
import { InitialFiltersMarketPremiumState } from '@state/reducers/initial-filter-market-premium/InitialFilterMarketPremiumReducer';
import { unhandledEnum } from './CompileTimeSafety';
import { SetStateAction } from 'react';
import { getLocalStorage, StorageKey } from '@utils/StorageUtils';

const errorMessageInvalidUser = 'Invalid user';
const errorMessageInvalidRetailer = 'Client do not have valid retailer Id';
const errorMessageInvalidSubscription = 'Client do not have valid country subscription';

export const checkIsSameUser = (
  user,
  pagesConfig: PagesConfig,
  updateSideFilters: (payload: SideFiltersState) => (dispatch: any) => void,
) => {
  const sessionUserId = getLocalStorage(StorageKey.UserData);
  const currentUserId = user?.sub;
  if (sessionUserId !== currentUserId || user?.isInternalUser) {
    resetAppliedFilters(pagesConfig, updateSideFilters);
    return false;
  }
  return true;
};

// resets applied filters in redux, session storage, and context
export const resetAppliedFilters = (
  pagesConfig: PagesConfig,
  updateSideFilters: (payload: SideFiltersState) => (dispatch: any) => void,
) => {
  const storeFilterSettings = {
    appliedFilters: cloneDeep(appliedFiltersInitialState),
  };
  pagesConfig.setSelectedFilters(cloneDeep(appliedFiltersInitialState));
  pagesConfig.setAppliedFilters(cloneDeep(appliedFiltersInitialState));
  updateSideFilters(storeFilterSettings);
};

export const validateRetailer = (
  productType: ProductType,
  subscriptions?: ClientSubscription[],
  reduxRetailerClientState?: any,
): number => {
  if (!subscriptions || subscriptions.length === 0) {
    saveErrorLog(new Error(errorMessageInvalidUser), `App - validate ${productType} retailer error.`, {
      category: ErrorTagEnum.DataError,
      requestPayload: { subscriptions: subscriptions },
      clientId: reduxRetailerClientState?.clientId,
    });
    throw new Error(errorMessageInvalidUser);
  }

  subscriptions.sort((a, b) => a.retailer.valueOf() - b.retailer.valueOf());
  const retailer = subscriptions[0].retailer;
  if (!retailer) {
    saveErrorLog(new Error(errorMessageInvalidRetailer), `App -validate ${productType} retailer error.`, {
      category: ErrorTagEnum.DataError,
      requestPayload: { subscriptions: subscriptions },
      clientId: reduxRetailerClientState?.clientId,
    });
    throw new Error(errorMessageInvalidRetailer);
  }

  return retailer;
};

export const validateRetailerCountries = (retailerCountries: Country[], productType: ProductType): void => {
  if (!retailerCountries?.length) {
    saveErrorLog(new Error(errorMessageInvalidSubscription), `App - validate ${productType} retailer country error.`, {
      category: ErrorTagEnum.DataError,
      requestPayload: { retailerCountries: retailerCountries },
    });
    throw new Error(errorMessageInvalidSubscription);
  }
};

// if its not the same user, take from subscription, otherwise take from redux
const getCorrectClientIdAndCountries = (
  reduxRetailerClientState: any,
  defaultRetailer: number,
  retailerCountries: Country[],
  isSameUser: boolean,
) => {
  if (!isSameUser) {
    return {
      defaultRetailer,
      retailerCountries,
    };
  }
  return {
    defaultRetailer: reduxRetailerClientState.clientId,
    retailerCountries: reduxRetailerClientState.countries,
  };
};

export const validateSubscriptionAndGetRetailerInfo = (
  productType: ProductType,
  subscriptions: ClientSubscription[],
  reduxRetailerClientState: any,
  updateState: (
    payload: RetailerClientState | HitlistsRetailerClientState | SellerInsightsRetailerClientState,
  ) => (dispatch: any) => void,
  pagesConfig: PagesConfig,
  updateSideFilters: (payload: SideFiltersState) => (dispatch: any) => void,
  isSameUser: boolean,
) => {
  let defaultRetailer: number;
  let retailerCountries: Country[];

  // Retrieve and validate clientId + countries from subscription
  defaultRetailer = validateRetailer(productType, subscriptions, reduxRetailerClientState);

  // remove duplicate countries after getting them from subscription
  retailerCountries = sortedUniqBy(
    sortBy(
      subscriptions
        .filter((subscription) => subscription.retailer === defaultRetailer)
        .flatMap((subscription) => ({
          id: subscription.countryObject.countryCode,
          name: subscription.countryObject.countryName,
          currency: subscription.countryObject.currency.currencyCode,
          currencyLabel: subscription.countryObject.currency.currencyLabel,
        })),
      (e) => e.name,
    ),
    'id',
  );

  // check if id from redux exist in subscription or not
  const subscriptionIndex = subscriptions.findIndex(
    (subscription) => subscription.retailer === reduxRetailerClientState.clientId,
  );

  if (subscriptionIndex === -1) {
    // if it is diff, we should reset applied filters. this usually occurs when we logout and login to diff user in the same browser
    resetAppliedFilters(pagesConfig, updateSideFilters);
  } else {
    // Get state clientId + countries from Redux store
    const correctClientIdAndCountries = getCorrectClientIdAndCountries(
      reduxRetailerClientState,
      defaultRetailer,
      retailerCountries,
      isSameUser,
    );
    defaultRetailer = correctClientIdAndCountries.defaultRetailer;
    retailerCountries = correctClientIdAndCountries.retailerCountries;
  }
  // Dispatch to Redux store to update clientId + countries
  updateState(constructRetailerClientState(defaultRetailer, retailerCountries));

  const retailerList = constructRetailerList(subscriptions);

  validateRetailerCountries(retailerCountries, productType);

  return {
    defaultRetailer: defaultRetailer,
    retailerCountries: retailerCountries,
    retailerList: retailerList,
  };
};

export const validateFilters = (mainFiltersResponse: MainFilters[]) => {
  mainFiltersResponse.forEach((filter) => {
    if (filter.timeFrame.weekly === 'Weekly' && filter.timePeriod.weekly.length === 0) {
      filter.timeFrame.weekly = null;
    }
    if (filter.timeFrame.monthly === 'Monthly' && filter.timePeriod.monthly.length === 0) {
      filter.timeFrame.monthly = null;
    }
  });
  return mainFiltersResponse;
};

export const getAndStoreSavedFilters = async (
  productType: ProductType,
  retailerId: number,
  pagesConfig: PagesConfig,
  userId: string | undefined,
) => {
  try {
    await getSavedFilters(userId, retailerId, productType);
  } catch (err: any) {
    saveErrorLog(err, `App - Get ${productType} saved filters error.`, {
      category: ErrorTagEnum.ApiError,
      requestPayload: { defaultRetailer: retailerId },
    });
  }
};

export const getFiltersAndRetailerClients = async (param: FiltersAndRetailerParam) => {
  await fetchFilters(param);
  await fetchRetailerClientByClientId(param);
};

const fetchFilters = async (param: FiltersAndRetailerParam) => {
  const {
    pageName,
    retailerId,
    retailerCountries,
    productType,
    productDataType,
    updateInitialFilters,
    updateInitialFiltersProducts,
    setLoading,
    updateInitialFiltersMarketPremium,
    productGroups,
  } = param;
  try {
    const mainFilters = await getFilters({
      clientId: retailerId,
      countries: retailerCountries,
      productType: productType,
      productDataType: productDataType,
      productGroups: productGroups,
    });

    if (!mainFilters) {
      return;
    }

    switch (pageName) {
      case PageName.RETAILER_OVERVIEW:
      case PageName.CATEGORY_LEADERBOARD:
      case PageName.OPPORTUNITY_REVIEW:
      case PageName.PERFORMANCE_ANALYSIS:
      case PageName.EXPLORE:
        updateInitialFilters({ initialFilters: validateFilters(mainFilters) });
        break;
      case PageName.RI_PRODUCT:
      case PageName.RI_PRODUCT_COMPARE:
      case PageName.RI_PRODUCT_TRENDS:
        updateInitialFiltersProducts({ initialFilters: validateFilters(mainFilters) });
        break;
      case PageName.TRADING_ASSISTANT:
        updateInitialFiltersMarketPremium({ initialFilters: validateFilters(mainFilters) });
        break;
      default: {
        unhandledEnum(pageName);
      }
    }
  } catch (err: any) {
    setLoading(false);
    saveErrorLog(err, 'App - Get filters error.', {
      category: ErrorTagEnum.ApiError,
      requestPayload: {
        defaultRetailer: retailerId,
        retailerCountries: retailerCountries,
      },
    });
  }
};

const fetchRetailerClientByClientId = async (param: FiltersAndRetailerParam) => {
  const {
    retailerList,
    productType,
    setRetailerClient,
    setHitlistsRetailerClient,
    setSellerInsightsRetailerClient,
    page,
    clientSubscription,
    setLoading,
    retailerCountries,
  } = param;
  try {
    const retailerClients = await getRetailerClientByClientId(retailerList, productType);
    if (retailerClients) {
      if (
        isRetailInsightsOrMarketPremiumSubscription(productType) ||
        isRetailKadSubscription(productType) ||
        isProductAnalyzerSubscription(productType)
      ) {
        setRetailerClient(retailerClients);
        setLoading(false);
      } else if (productType === ProductType.SellerInsights) {
        setSellerInsightsRetailerClient(retailerClients);
      } else {
        setHitlistsRetailerClient(retailerClients);
      }
      page('Logged in home page opened');
    }
  } catch (err: any) {
    saveErrorLog(err, `App - Get ${productType} retailer client error.`, {
      category: ErrorTagEnum.ApiError,
      requestPayload: {
        clientSubscription: clientSubscription,
        retailerCountries: retailerCountries,
      },
    });
    setLoading(false);
  }
};

export interface FiltersAndRetailerParam {
  pageName: PageName;
  productType: ProductType;
  productDataType: ProductDataType;
  productGroups?: Option[];
  retailerId: number;
  retailerCountries: Country[];
  retailerList: number[];
  clientSubscription: OpaSubscription;
  setRetailerClient: (value: SetStateAction<RetailerClient[]>) => void;
  setLoading: (value: SetStateAction<boolean>) => void;
  setHitlistsRetailerClient: (value: SetStateAction<RetailerClient[]>) => void;
  setSellerInsightsRetailerClient: (value: SetStateAction<RetailerClient[]>) => void;
  page: (name: string, properties?: Object | undefined) => void;
  updateInitialFilters: (payload: InitialFiltersState) => (dispatch: any) => void;
  updateInitialFiltersProducts: (payload: InitialFiltersProductsState) => (dispatch: any) => void;
  updateInitialFiltersMarketPremium: (payload: InitialFiltersMarketPremiumState) => (dispatch: any) => void;
}

export type SubscriptionDetail = {
  currentClientCountries: Country[];
  currentSubscription: OpaSubscription;
  currentClientId: number;
};

export const getSubscriptionDetailsByProductType = (
  productType: ProductType,
  pagesConfig: PagesConfig,
): SubscriptionDetail => {
  if (ProductType.RetailKad === productType) {
    return {
      currentClientCountries: pagesConfig.clientCountries,
      currentSubscription: pagesConfig.retailKadClientSubscription,
      currentClientId: pagesConfig.clientId,
    };
  }

  if (ProductType.PerformanceAnalyzer === productType) {
    return {
      currentClientCountries: pagesConfig.clientCountries,
      currentSubscription: pagesConfig.clientSubscription,
      currentClientId: pagesConfig.clientId,
    };
  }

  if (ProductType.MarketPremium === productType) {
    return {
      currentClientCountries: pagesConfig.marketPremiumClientCountries,
      currentSubscription: pagesConfig.marketPremiumClientSubscription,
      currentClientId: pagesConfig.marketPremiumClientId,
    };
  }

  if (ProductType.SellerInsights === productType) {
    return {
      currentClientCountries: pagesConfig.sellerInsightsClientCountries,
      currentSubscription: pagesConfig.sellerInsightsClientSubscription,
      currentClientId: pagesConfig.sellerInsightsClientId,
    };
  }

  return {
    currentClientCountries: pagesConfig.hitlistsClientCountries,
    currentSubscription: pagesConfig.hitlistsClientSubscription,
    currentClientId: pagesConfig.hitlistsClientId,
  };
};

/**
 * Finds and returns a subscription object by country.
 *
 * @param pagesConfig - A reference to the pagesConfig object.
 * @param country - The selected country
 */
export const findSubscriptionByCountry = (pagesConfig: PagesConfig, country: Option) => {
  const subscriptions = [
    pagesConfig.retailKadClientSubscription,
    pagesConfig.hitlistsClientSubscription,
    pagesConfig.sellerInsightsClientSubscription,
    pagesConfig.marketPremiumClientSubscription,
    pagesConfig.clientSubscription,
  ];

  for (const subscription of subscriptions) {
    const match = subscription?.result?.restrictions.find(
      (client) => client.countryObject.countryCode === country.value,
    );

    if (match) {
      return match;
    }
  }
};

export const getClientSubscriptionBasedOnRetailKAD = async (
  productDataType: ProductDataType,
  getRetailKADClientSubscriptionFunc?: (productDataType: ProductDataType) => Promise<OpaSubscription>,
  getClientSubscriptionFunc?: () => Promise<OpaSubscription>,
) => {
  return getRetailKADClientSubscriptionFunc
    ? getRetailKADClientSubscriptionFunc(productDataType)
    : getClientSubscriptionFunc!();
};
